import { useEffect, useMemo, useState } from "react";

import _ from "lodash";

import { EDIT, CLICK, CLEAR } from "app/pages/website-analysis/website-technologies/constants/constants";
import { chosenSites } from "app/common/services/chosenSites";
import type { TechnologyItem } from "app/pages/workspace/sales/sub-modules/feed/types/technologies";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import { allTrackers } from "app/services/track/track";

import { CategoryFilter } from "./filters/CategoryFilter";
import { SearchTechnology } from "./filters/SearchTechnology";
import { StatusFilter } from "./filters/StatusFilter";
import { SubDomainFilter } from "./filters/SubDomainFilter";
import { PageContent, StyledFiltersWrapper } from "./styles";
import { fetchSiteTechnologies, technologiesExcelUrl } from "./technologiesApi";
import { enrichWithIsUsedInDomain, getSortedSubdomainCounts } from "./technologyList";
import { StatusFilterValue } from "./types";
import { WebsiteTechnologiesTable } from "./WebsiteTechnologiesTable";

export const WebsiteTechnologiesPage = () => {
    const [category, onSelectCategory] = useState<string | null>(null);
    const [searchString, onSearch] = useState<string>("");
    const [isSubDomainFilterDisabled, setIsSubDomainFilterDisabled] = useState(false);
    const [statusFilterValue, setStatusFilterValue] = useState(StatusFilterValue.InstalledAndRemoved);

    const [subdomainCounts, setSubdomainCounts] = useState<Record<string, number>>({});
    const [selectedSubdomains, setSelectedSubdomains] = useState<string[]>([]);

    const [technologyList, setTechnologyList] = useState<TechnologyItem[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const { key } = proNavigator.getParams();
    const domains = useMemo(() => key.split(","), [key]);
    const excelUrl = useMemo(() => technologiesExcelUrl(domains), [domains]);

    async function fetchData() {
        try {
            setIsLoading(true);

            const technologies = await fetchSiteTechnologies(domains);
            const enrichedTechnologies = enrichWithIsUsedInDomain(technologies, domains);
            const subdomainCounts = getSortedSubdomainCounts(enrichedTechnologies);
            const subdomains = Object.keys(subdomainCounts);

            setSubdomainCounts(subdomainCounts);
            setIsSubDomainFilterDisabled(subdomains.length < 1);
            setTechnologyList(enrichedTechnologies);
            setSelectedSubdomains(subdomains);
        } catch (e) {
        } finally {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        fetchData();
        setStatusFilterValue(StatusFilterValue.InstalledAndRemoved);
    }, [key]);

    function onCategoryChanged(category) {
        const type = category === null ? CLEAR : `select/${category.text}`;
        allTrackers.dispatchTrackEvent("Drop Down", CLICK, `${type || ""}`);
        onSelectCategory(category ? category.id : null);
    }

    const handleSelectSubdomains = (selectedSubdomains: string[]) => {
        setSelectedSubdomains(selectedSubdomains);
    };

    const handleClearSubdomainsFilter = () => {
        setSelectedSubdomains(Object.keys(subdomainCounts));
    };

    const trackSearch = useMemo(
        () =>
            _.debounce<(term) => void>((term) => {
                if (term) {
                    allTrackers.dispatchTrackEvent("Search Bar", EDIT, `search/${term || ""}`);
                }
            }, 500),
        [],
    );

    function searchCallback({ target: { value } }) {
        trackSearch(value || "");
        onSearch(value || "");
    }

    return (
        <article className="technographics-page-wrapper">
            <PageContent>
                <StyledFiltersWrapper>
                    <CategoryFilter
                        onSelect={onCategoryChanged}
                        category={category}
                        rawData={technologyList}
                        onClear={() => onCategoryChanged(null)}
                    />
                    <SubDomainFilter
                        disabled={isSubDomainFilterDisabled}
                        onSelect={handleSelectSubdomains}
                        data={subdomainCounts}
                        onClear={handleClearSubdomainsFilter}
                    />
                    {domains.length === 1 ? (
                        <StatusFilter
                            value={statusFilterValue}
                            onChange={(value) => setStatusFilterValue(value)}
                        />
                    ) : null}
                </StyledFiltersWrapper>
                <SearchTechnology
                    value={searchString}
                    onSearch={searchCallback}
                    excelUrl={excelUrl}
                />
                <WebsiteTechnologiesTable
                    isLoading={isLoading}
                    keys={domains}
                    rawData={technologyList}
                    selectedCategory={category}
                    selectedSubdomains={selectedSubdomains}
                    searchString={searchString}
                    getSiteImage={(site) => chosenSites.listInfo[site].icon}
                    statusFilterValue={statusFilterValue}
                />
            </PageContent>
        </article>
    );
};
